import type { BasicContent } from '$lib/domain/basic-content.interface';
import type { Service } from '$lib/domain/service.interface';
import type { IconTitle } from '$lib/domain/icon-title.interface';
import type { Review } from '$lib/domain/review.interface';

export const caseStudyArticlesList: string [] = [
  '/our-works/business-digitalization-for-a-chain-of-canadian-medical-clinics/',
  '/our-works/formula-compounder/',
  '/our-works/medical-information-system-software/',
];
export const caseStudyArticlesReviews: Review[] = [
  {
    title: 'A mobile app and web portal for a chain of clinics',
    text: 'We developed these software solutions for a chain of medical clinics in Canada and Mexico. Seeking to embrace digitalization and introduce telemedicine, the client asked us to build an app with features like personal profiles, access to medical records, and scheduling. Additionally, we had to develop a web portal to automate basic tasks for doctors and medical staff. Our team successfully implemented this project from scratch, enabling the client to expand their reach with remote services.',
    relatedArticleUrls: ['business-digitalization-for-a-chain-of-canadian-medical-clinics']
  },
  {
    title: 'A LIMS for a pharmaceutical company',
    text: 'Our team built this robust laboratory information management system (LIMS) for a European pharmaceutical company struggling to streamline its drug manufacturing process. The LIMS offers editable templates for simplified formula compounding, formula usage tracking, a component database, and more. Prior to building this solution, we did thorough market research and mapped the user journey. The client highly appreciated our work and provided positive feedback.',
    relatedArticleUrls: ['formula-compounder']
  },
  {
    title: 'A medical information system',
    text: 'We developed this solution for a client whose aim was to modernize healthcare organizations by transforming clinician-patient interactions and streamlining hospital workflows. Building this medical information system from the ground up, our team implemented video conferencing, in-app messaging, notifications, and other features. Ensuring robust data protection was our top priority.',
    relatedArticleUrls: ['medical-information-system-software']
  },
]
export const relatedUrlList: string [] = [
  '/hipaa-compliant-telehealth-platform/',
  '/benefits-of-telehealth-for-patients-doctors/',
  '/our-works/custom-telemedicine-solution/',
  '/the-pillars-of-a-winning-telehealth-strategy/'
];
export const services: Service[] = [
  {
    title: 'Custom telemedicine software development',
    content: 'We can build a custom telemedicine platform from the ground up, incorporating specific features your hospital or clinic needs. Our telemedicine software development company will guide you through the entire process, from defining requirements to post-release support.',
    iconName: 'telemedicine',
    url: '',
  },
  {
    title: 'UI/UX design',
    content: 'Our team can create intuitive, visually appealing interfaces for your telemedicine app, ensuring enhanced usability for all user categories. We will take care of a compelling look for all elements, a clear and logical user journey, easy navigation, responsiveness, and accessibility.',
    iconName: 'uiUxDesign',
    url: '',
  },
  {
    title: 'EHR integration',
    content: 'As part of our telemedicine app development services, we can integrate the new platform with EHR software used by your organization. This will allow clinicians to easily access patients’ full medical information during telemedicine consultations and ensure a holistic approach to treatment.',
    iconName: 'EmrEhrSolutions',
    url: '',
  },
  {
    title: 'Payment system integration',
    content: 'We\'ll connect your telemedicine platform to payment gateways, allowing patients to easily pay for remote medical services online. This integration will simplify billing, streamline financial transactions, and reduce paperwork.',
    iconName: 'digitalBankingApp',
    url: '',
  },
  {
    title: 'Telemedicine platform customization',
    content: 'Our telemedicine app developers can tailor an off-the-shelf platform to fit the unique needs of your clinic or hospital. We\'ll customize its appearance, tweak the features to match your existing workflows, and seamlessly integrate it with other systems in your IT infrastructure.',
    iconName: 'integrations',
    url: '',
  },
  {
    title: 'Ongoing support and maintenance',
    content: 'Our telemedicine app development company can support your solution after its launch. We will fix bugs and glitches, implement updates and upgrades, continuously monitor the performance, regularly reinforce security measures, and take care of data backups.',
    iconName: 'techSupport',
    url: '',
  },
];
export const expertises: Service[] = [
  {
    title: 'Remote patient monitoring',
    content: 'With remote patient monitoring, clinicians can keep track of patients’ vital signs, symptoms, and health metrics remotely. Relying on the collection of health data from connected devices, this functionality enhances both in-hospital care and the management of health conditions outside traditional healthcare settings.',
    iconName: 'patientMonitoring',
    url:'',
  },
  {
    title: 'Real-time communication',
    content: 'Being the heart of telemedicine platforms, real-time communication includes video and audio conferencing, secure messaging, file and image sharing, screen sharing, and more. This functionality connects doctors and patients, making virtual consultations as interactive and responsive as in-person visits.',
    iconName: 'support',
    url:'',
  },
  {
    title: 'e-Prescriptions',
    content: 'e-Prescription allows doctors to easily create and send prescriptions to patients or pharmacies directly from the telemedicine platform. Eliminating the need for handwritten or printed prescriptions, it streamlines telemedicine services, makes the healthcare experience more convenient, and helps digitize patients’ medication records.',
    iconName: 'treatment',
    url:'',
  },
  {
    title: 'Online appointment booking',
    content: 'With online booking, patients can easily schedule virtual appointments with doctors using a telemedicine app. This feature may include a digital calendar displaying available time slots, instant confirmation, automated reminders, waitlists for fully booked appointments, and more.',
    iconName: 'onlineAppointmentBooking',
    url:'',
  },
  {
    title: 'Doctor schedule management',
    content: 'Doctor schedule management allows clinicians to efficiently plan their virtual appointments and manage available time slots. Its functions typically include a calendar, appointment history records, notifications, and patient notes. All this makes the telemedicine process well-organized and convenient for everyone.',
    iconName: 'mvp',
    url:'',
  },
  {
    title: 'Audio and video recording',
    content: 'This feature lets healthcare providers record audio and video during virtual consultations. This way, they can create detailed documentation of patient interactions, review recordings to ensure that doctors follow professional standards, and analyze consultations for clinical decision support.',
    iconName: 'audioVideoRecording',
    url:'',
  },
];
export const benefits: IconTitle[] = [
  {
    title: 'Extensive software development experience',
    iconName: 'concept'
  },
  {
    title: 'Healthcare domain expertise',
    iconName: 'treatment'
  },
  {
    title: 'Telemedicine development portfolio',
    iconName: 'telemedicine'
  },
  {
    title: 'Tech-savvy team',
    iconName: 'dedicatedTeam'
  },
  {
    title: 'Client satisfaction as the top priority',
    iconName: 'client'
  },
  {
    title: 'Outstanding rates/quality ratio',
    iconName: 'positiveReview'
  },
];
export const approach: BasicContent[]  = [
  {
    title: 'Innovation',
    content: 'Innovation is at the core of all our activities as a leading telemedicine app development company. We continually strive to digitalize healthcare by building high-quality telemedicine platforms and implementing cutting-edge functionality, like video conferencing and remote patient monitoring. Commissioning our telemedicine application development services, medical organizations transform care delivery as we know it and improve accessibility to clinical services.'
  },
  {
    title: 'Agility',
    content: 'Agility forms a foundation of our approach to delivering telemedicine app development solutions. Using agile methodologies, we can respond promptly to the evolving needs of healthcare providers and clients. We navigate complexities with ease, delivering solutions that align with security requirements and medical regulations. Embrace agility with our telemedicine software development company and stay ahead of the curve in the rapidly evolving healthtech domain.'
  },
  {
    title: 'Expertise',
    content: 'Unparalleled expertise is wat sets us apart from other healthcare software engineers in the industry. Our team of seasoned telemedicine app developers have a profound understanding of the medical sector and exceptional technical skills. This allows us to build top-notch telemedicine solutions that not just meet but exceed expectations. Entrust your telemedicine software development project to us, and we\'ll handle all technical aspects and assist you in project decision-making.'
  }
];
